<form [formGroup]="form">
  <div class="row g-2 mb-2">
    <div class="col-12 col-xl-6">
      <bn-web-ui-card
        containerClass="m-2"
        heading="Bot detection"
        inputType="switch"
        [headingBadges]="[
          {
            context: 'premium',
            text: 'Advanced',
            size: 'sm',
          },
        ]"
        [inputValue]="globalBotDetection() || false"
        [isFullHeight]="true"
        [tooltip]="selectedShield()?.planType === PlanType.Basic ? upgradeToAdvancedTooltipTmpl : ''"
        (valueChanged)="onGlobalBotDetectionChange($event)">
        <p class="bn-container--md">
          Bunny Bot Detection intelligently identifies and mitigates malicious or unwanted automated traffic before it reaches your origin. Protect your website
          from credential stuffing, scraping, abuse, and non-human interactions that can degrade performance or compromise security.
        </p>
      </bn-web-ui-card>
    </div>
    <div class="col-12 col-xl-6">
      <bn-web-ui-card
        heading="Rule execution mode"
        [disabled]="!globalBotDetection() && selectedShield()?.planType === PlanType.Basic"
        [isFullHeight]="true">
        <div class="mb-4">
          <p>Choose whether to log detected bots or actively challenge them during detection.</p>
        </div>
        <div class="row">
          <bn-web-ui-radio-tabs
            formControlName="executionMode"
            [isLoading]="isUpdating.executionMode()"
            [items]="executionModeItems" />
        </div>
      </bn-web-ui-card>
    </div>
  </div>

  <bn-web-ui-card
    class="mb-2"
    heading="Bot detection sensitivity"
    [disabled]="!globalBotDetection() && selectedShield()?.planType === PlanType.Basic">
    <p>Defines how strictly bot activity is detected based on request integrity, IP reputation, and browser fingerprinting.</p>
    <div class="mt-3">
      <h2 class="mb-4">Sensitivity</h2>
    </div>
    <div class="sensitivity-picker mb-4">
      <app-app-shield-sensitivity-picker
        [isLoading]="isLoading() || isUpdating.templateSensitivity()"
        [items]="levelTemplates"
        (itemClick)="onSelectSensitivityTemplate($event)" />
    </div>

    <div
      class="flex-column d-flex gap-6 pt-3"
      [formGroup]="levels">
      <div class="row d-flex justify-content-between">
        <div class="col-xl-6">
          <h3 class="d-flex">
            <bn-web-ui-switch
              class="me-2"
              formControlName="requestIntegrityLevel_Switch"
              label="Request integrity"
              size="sm" />
          </h3>
          <p class="mt-1 mb-2 mb-xl-0">Analyzes request headers, query structure, and protocol anomalies.</p>
        </div>
        <div class="col-xl-5 align-content-center user-select-none">
          <bn-web-ui-level
            customLoadingHeight="42px"
            formControlName="requestIntegrityLevel"
            [isLoading]="isLoading() || isUpdating.requestIntegrityLevel() || isUpdating.templateSensitivity()"
            [levels]="levelsItems" />
        </div>
      </div>

      <div class="row d-flex justify-content-between">
        <div class="col-xl-6">
          <h3 class="d-flex">
            <bn-web-ui-switch
              class="me-2"
              formControlName="ipAddressLevel_Switch"
              label="IP address"
              size="sm" />
          </h3>
          <p class="mt-1 mb-2 mb-xl-0">Monitors IP behaviour, reputation, and rate patterns.</p>
        </div>
        <div class="col-xl-5 align-content-center user-select-none">
          <bn-web-ui-level
            customLoadingHeight="42px"
            formControlName="ipAddressLevel"
            [isLoading]="isLoading() || isUpdating.ipAddressLevel() || isUpdating.templateSensitivity()"
            [levels]="levelsItems" />
        </div>
      </div>

      <div class="row d-flex justify-content-between">
        <div class="col-xl-6">
          <h3 class="d-flex">
            <bn-web-ui-switch
              class="me-2"
              formControlName="fingerprintSensitivityLevel_Switch"
              label="Fingerprint sensitivity"
              size="sm" />
          </h3>
          <p class="mt-1 mb-2 mb-xl-0">Adjusts how precisely browsers are checked for signs of automation.</p>
        </div>
        <div class="col-xl-5 align-content-center user-select-none">
          <bn-web-ui-level
            customLoadingHeight="42px"
            formControlName="fingerprintSensitivityLevel"
            [isLoading]="isLoading() || isUpdating.fingerprintSensitivityLevel() || isUpdating.templateSensitivity()"
            [levels]="levelsItems" />
        </div>
      </div>

      <div class="row d-flex justify-content-between">
        <div
          class="col-xl-6"
          [ngClass]="{
            'bn-app-shield-bot-detection__title--disabled': levels.controls.fingerprintAggressionLevel.disabled,
          }">
          <h3 class="d-flex text-dark-7">Fingerprint aggression</h3>
          <p class="mt-1 mb-2 mb-xl-0">Controls how assertively unusual fingerprints are treated as bots.</p>
        </div>
        <div class="col-xl-5 align-content-center user-select-none">
          <bn-web-ui-level
            customLoadingHeight="42px"
            formControlName="fingerprintAggressionLevel"
            [isLoading]="isLoading() || isUpdating.fingerprintAggressionLevel() || isUpdating.templateSensitivity()"
            [levels]="levelsFingerprintAggressionItems" />
        </div>
      </div>

      <div class="row d-flex justify-content-between">
        <div class="col-xl-6 position-relative">
          <h3 class="d-flex">
            <bn-web-ui-switch
              class="me-2"
              containerClass="m-2"
              formControlName="fingerprintComplex"
              label="Complex fingerprinting"
              size="sm"
              [loading]="isUpdating.fingerprintComplex()"
              [tooltip]="(selectedShield()?.planType || 0) < PlanType.Business ? upgradeToBusinessTooltipTmpl : ''" />
            <bn-web-ui-badge
              color="premium"
              size="sm"
              >Business</bn-web-ui-badge
            >
          </h3>
          <p class="mt-1 mb-2 mb-xl-0">Combines advanced entropy analysis and cross-session consistency.</p>
        </div>
      </div>
    </div>
  </bn-web-ui-card>
</form>

<bn-web-ui-card
  class="mb-2"
  [disabled]="!globalBotDetection() && selectedShield()?.planType === PlanType.Basic"
  [noPadding]="true">
  <div class="row g-0">
    <div class="col-6 border-right border-dark-2 p-5">
      <p class="text-200-regular text-bunny-dark mb-1">Total logged requests</p>
      <p class="text-700-regular text-bunny-dark">{{ (logged() | metricFormat).formatted }}</p>
    </div>
    <div class="col-6 p-5">
      <p class="text-200-regular text-bunny-dark mb-1">Total challenges served</p>
      <p class="text-700-regular text-bunny-dark">{{ (challengesServed() | metricFormat).formatted }}</p>
    </div>
  </div>
</bn-web-ui-card>

<div class="my-2">
  <bn-web-ui-card
    heading="Bot activity"
    [disabled]="!globalBotDetection() && selectedShield()?.planType === PlanType.Basic">
    <div class="chart-container position-relative">
      <bn-chart-multi-metric
        [chartConfig]="{
          formatMode: 'metricSymbol',
          summaryLabels: [
            { key: 'logged', label: 'Logged', color: 'info' },
            { key: 'challengesServed', label: 'Challenges served', color: 'success' },
          ],
        }"
        [data]="botDetectionChart().chartSeries"
        [summaryLabels]="botDetectionChart().total" />
      @if (!botDetectionChartHasData()) {
        <div class="chart-overlay">
          <span>There is no attack data to show yet.</span>
        </div>
      }
    </div>
  </bn-web-ui-card>
</div>

<bn-web-ui-documentation
  [linksFirst]="linksFirst"
  [titleFirst]="titleFirst" />

<ng-template #upgradeToAdvancedTooltipTmpl>Upgrade to Advanced plan<br />to use this feature.</ng-template>
<ng-template #upgradeToBusinessTooltipTmpl>Upgrade to Business plan<br />to use this feature.</ng-template>
